import type { FieldMapping, TransformationStep } from '../../services/api';

export const EDGE_COLOR_PALETTE = ['#2563eb', '#0ea5e9', '#3b82f6', '#1d4ed8', '#ec4899', '#f97316', '#f59e0b', '#10b981', '#22c55e', '#14b8a6'];

export const EDGE_COLOR = '#b1b1b7';

export const clamp = (value: number, min: number, max: number): number => {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

export const stableHash = (raw: string): number => {
  let hash = 0;
  for (let index = 0; index < raw.length; index += 1) {
    hash = (hash * 31 + raw.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
};

export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const normalised = hex.replace('#', '');
  const bigint = parseInt(normalised, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};

export const mixHexColors = (foreground: string, background: string, ratio: number): string => {
  const safeRatio = clamp(ratio, 0, 1);
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  const r = Math.round(fg.r * (1 - safeRatio) + bg.r * safeRatio);
  const g = Math.round(fg.g * (1 - safeRatio) + bg.g * safeRatio);
  const b = Math.round(fg.b * (1 - safeRatio) + bg.b * safeRatio);
  return `rgb(${r}, ${g}, ${b})`;
};

export const edgePaletteColor = (mapping: FieldMapping, step?: TransformationStep): string => {
  const key = step?.step_id ? `${mapping.mapping_id}:${step.step_id}` : `${mapping.mapping_id}:${mapping.target_table}.${mapping.target_field}`;
  const index = stableHash(key) % EDGE_COLOR_PALETTE.length;
  return EDGE_COLOR_PALETTE[index];
};

export const edgeStrokeColor = (mapping: FieldMapping, step: TransformationStep | undefined, confidence?: number, explicitColor?: string): string => {
  if (explicitColor) return explicitColor;

  // Schema evolution status-based coloring (overrides confidence)
  const fieldStatus = mapping.field_status;
  if (fieldStatus === 'inherited') return '#3b82f6'; // Blue (Inherited/Locked)
  if (fieldStatus === 'broken')    return '#ef4444'; // Red (Broken)
  if (fieldStatus === 'stale')     return '#f59e0b'; // Amber (Stale)
  if (fieldStatus === 'approved')  return '#10b981'; // Green (Approved)

  const safeConfidence = clamp(typeof confidence === 'number' && !Number.isNaN(confidence) ? confidence : mapping.confidence ?? 0.65, 0, 1);

  // Traffic Light Logic (for ai_generated or unset field_status)
  if (safeConfidence >= 0.9) return '#10b981'; // Green (Safe)
  if (safeConfidence >= 0.6) return '#f59e0b'; // Amber (Review)
  return '#ef4444'; // Red (Danger)
};

/** Return strokeDasharray style based on field_status, falling back to confidence-based dashing. */
export const edgeStrokeDash = (mapping: FieldMapping, confidence?: number): { strokeDasharray?: string } => {
  const fs = mapping.field_status;
  if (fs === 'inherited' || fs === 'approved') return {};              // Solid
  if (fs === 'broken')  return { strokeDasharray: '8,4' };            // Thick dashed
  if (fs === 'stale')   return { strokeDasharray: '3,3' };            // Dotted
  // Default: confidence-based (existing behavior)
  if (confidence !== undefined && confidence < 0.9) return { strokeDasharray: '5,5' };
  return {};
};
